import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, Typography } from '@hpstellar/core';

import withPageComponents from '../../../page/components/with-page-components';
import RadioOptionGroup from './components/radio-option-group';
import AlertMessage from '../../../shared/components/alert-message';
import SimilarProductsSection from '../pdp-details/similar-products-section';
import useTemplateTheme from '../../../hooks/useTemplateTheme';
import { scrollToSpecsAndConditionallyLockView } from '../../util';
import { NAV_SECTIONS } from '../constants';
const { techSpecs: specsNAV } = NAV_SECTIONS;

import './cto-options.less';

const DEFAULT_TRANSLATION = {
    label: 'YOUR CONFIGURATION',
    unavaiableTooltip: {
        description: 'Other options will be updated based on this selection.',
        mobileDescription: 'Updated other options based on this selection',
    },
};

const infoText =
    (unavailableGroup, infoText) =>
    ({ isDesktop, groupIdx }) => {
        if (isDesktop || groupIdx !== unavailableGroup) {
            return null;
        }
        return <AlertMessage message={infoText} variation="slim" />;
    };

/**
 * "Shadow" CTO component - simulates configurable options be linking options to existing product pages
 */
export default withPageComponents(
    ({ pdpShadowCTO, translations = DEFAULT_TRANSLATION, vanityUrl, isClientGQLLoading }) => {
        const {
            shadowCTOEnabled,
            shadowCTOOptions,
            shadowCTOFilter,
            specsLinkText = 'See all specs',
        } = pdpShadowCTO || {};
        if (!shadowCTOEnabled) {
            return null;
        }
        const [isLoading, setIsloading] = useState(false);
        const [unavailableGroup, setUnavailableGroup] = useState();
        const hasScrolledToSpecs = useRef(null);
        const { typography } = useTemplateTheme(['typography']);
        const { primaryButtonLinkClassName } = typography || {};
        useEffect(() => {
            isClientGQLLoading !== true && setIsloading(false);
        }, [vanityUrl, isClientGQLLoading]);
        const onRadioClick = useCallback(
            option => {
                setIsloading(true);
                if (option.isUnavailable) {
                    setUnavailableGroup(option.groupIdx);
                } else if (typeof unavailableGroup === 'number') {
                    setUnavailableGroup(null);
                }
            },
            [unavailableGroup],
        );
        const { label, unavaiableTooltip } = translations;

        const seeAllLinkClick = () => {
            try {
                scrollToSpecsAndConditionallyLockView(hasScrolledToSpecs && hasScrolledToSpecs.current, {
                    onComplete: () => {
                        try {
                            hasScrolledToSpecs.current = true;
                        } catch (e) {}
                    },
                });
            } catch (e) {}
        };

        return (
            <div className="cto-options-container">
                {shadowCTOFilter && (
                    <RadioOptionGroup
                        options={shadowCTOFilter}
                        defaultUnavalableTooltip={unavaiableTooltip}
                        onRadioClick={onRadioClick}
                        isLoading={isLoading}
                        content={infoText(unavailableGroup, unavaiableTooltip.mobileDescription)}
                    />
                )}
                <div className="shadow-divider dense"></div>
                <div className="cto-options-group">
                    <Typography className="cto-option-title" tag="h3" variant="bodyS">
                        {label}
                    </Typography>
                    <RadioOptionGroup
                        options={shadowCTOOptions}
                        defaultUnavalableTooltip={unavaiableTooltip}
                        onRadioClick={onRadioClick}
                        isLoading={isLoading}
                        disclaimer="*Price and promotions vary depending on selected configuration."
                        content={infoText(unavailableGroup, unavaiableTooltip.mobileDescription)}
                    />
                </div>
                <Link dense to={`#${specsNAV.hash}`} className={primaryButtonLinkClassName} onClick={seeAllLinkClick}>
                    {specsLinkText}
                </Link>
                <div className="shadow-divider"></div>
                <SimilarProductsSection />
            </div>
        );
    },
    { components: ['pdpShadowCTO'], gqlClientLoadStatus: true },
);
